(function () {
  'use strict';

  var ROOT_SELECTOR = '[data-smile-points-shop]';
  var PENDING_PRODUCT_KEY = 'gan-smile-points-shop:pending-product';
  var PRODUCT_CACHE_KEY = 'gan-smile-points-shop:products:v1';
  var SDK_TIMEOUT_MS = 45000;
  var SDK_POLL_MS = 100;
  var CUSTOMER_RETRY_MS = 500;
  var MAX_CUSTOMER_ATTEMPTS = 20;
  var GUEST_RETRY_MS = 750;
  var MAX_GUEST_ATTEMPTS = 3;
  var rewardPresentationCache;

  if (window.GanSmilePointsShop) {
    window.GanSmilePointsShop.init(document);
    return;
  }

  function isLoggedIn(root) {
    return root.dataset.customerLoggedIn === 'true';
  }

  function hasSmileMethods(root) {
    if (!window.Smile) return false;

    if (isLoggedIn(root)) {
      return Boolean(
        window.Smile.customerPointsProducts &&
          typeof window.Smile.customerPointsProducts.get === 'function' &&
          window.Smile.customerPointsWallet &&
          typeof window.Smile.customerPointsWallet.get === 'function' &&
          window.Smile.customer &&
          typeof window.Smile.customer.current === 'function'
      );
    }

    return Boolean(window.Smile.pointsProducts && typeof window.Smile.pointsProducts.get === 'function');
  }

  function setState(root, state, message) {
    var content = root.querySelector('[data-smile-points-shop-content]');
    var carousel = root.querySelector('[data-smile-points-shop-carousel]');
    var grid = root.querySelector('[data-smile-points-shop-grid]');
    var stateContainer = root.querySelector('[data-smile-points-shop-state]');
    var skeleton = root.querySelector('[data-smile-points-shop-skeleton]');
    var messageElement = root.querySelector('[data-smile-points-shop-message]');
    var retry = root.querySelector('[data-smile-points-shop-retry]');

    content.setAttribute('aria-busy', state === 'loading' ? 'true' : 'false');
    carousel.hidden = state !== 'ready';
    grid.hidden = state !== 'ready';
    stateContainer.hidden = state === 'ready';
    stateContainer.classList.toggle('smile-points-shop__state--loading', state === 'loading');
    skeleton.hidden = state !== 'loading';
    retry.hidden = state !== 'error';
    messageElement.textContent = message;

    if (state !== 'loading' && root.smilePointsShopTimeout) {
      window.clearTimeout(root.smilePointsShopTimeout);
      root.smilePointsShopTimeout = null;
    }
  }

  function announce(root, message) {
    var announcer = root.querySelector('[data-smile-points-shop-announcer]');
    if (announcer) announcer.textContent = message;
  }

  function formatPoints(amount) {
    if (amount === null || typeof amount === 'undefined' || Number.isNaN(Number(amount))) return '';

    if (window.Smile && typeof window.Smile.formatPoints === 'function') {
      try {
        var formatted = window.Smile.formatPoints(Number(amount));
        if (typeof formatted === 'string' && formatted) return formatted;
      } catch (error) {
        // Fall back to a stable label when Smile formatting is unavailable.
      }
    }

    return Number(amount).toLocaleString() + ' Points';
  }

  function normalizeProduct(item, customerBalance, customerSpecific) {
    var pointsProduct = customerSpecific ? item.pointsProduct || item.points_product || item : item;
    var reward = pointsProduct.reward || item.reward || {};
    var pointsPrice =
      item.pointsPrice ??
      item.points_price ??
      pointsProduct.pointsPrice ??
      pointsProduct.points_price ??
      pointsProduct.variablePointsMin ??
      pointsProduct.variable_points_min;
    var numericPrice = Number(pointsPrice);
    var hasPrice = pointsPrice !== null && typeof pointsPrice !== 'undefined' && !Number.isNaN(numericPrice);
    var productId = pointsProduct.id ?? item.pointsProductId ?? item.points_product_id ?? item.id;
    var productName = item.name || reward.name || pointsProduct.name || 'Reward';
    var imageUrl = reward.imageUrl || reward.image_url || item.imageUrl || item.image_url || '';
    var isVariable = pointsProduct.exchangeType === 'variable' || pointsProduct.exchange_type === 'variable';
    var canRedeem = !customerSpecific || !hasPrice || customerBalance >= numericPrice;
    var pointsLabel = hasPrice ? formatPoints(numericPrice) : pointsProduct.exchangeDescription || pointsProduct.exchange_description || '';

    if (isVariable && pointsLabel) pointsLabel = 'From ' + pointsLabel;

    return {
      id: productId === null || typeof productId === 'undefined' ? '' : String(productId),
      name: String(productName),
      imageUrl: String(imageUrl),
      description: String(reward.description || item.description || ''),
      pointsPrice: hasPrice ? numericPrice : null,
      pointsLabel: String(pointsLabel || 'View reward'),
      canRedeem: canRedeem,
    };
  }

  function isPhysicalReward(product) {
    return /icon-free-product/i.test(product.imageUrl);
  }

  function getRewardPresentationResolver() {
    if (rewardPresentationCache !== undefined) return rewardPresentationCache;
    var resolver = window.SmileRewardPresentation;
    if (
      !resolver ||
      typeof resolver.createShopifyCatalogAdapter !== 'function' ||
      typeof resolver.resolveRewardPresentation !== 'function'
    ) {
      rewardPresentationCache = null;
      return rewardPresentationCache;
    }
    var adapter = resolver.createShopifyCatalogAdapter({
      root: (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/',
    });
    var searchStore = new Map();
    var productStore = new Map();
    var searchCache = {
      get: function (key) { return searchStore.get(key); },
      set: function (key, value) { searchStore.set(key, value); },
    };
    var productCache = {
      get: function (key) { return productStore.get(key); },
      set: function (key, value) { productStore.set(key, value); },
    };
    rewardPresentationCache = function (reward) {
      return resolver.resolveRewardPresentation(
        { name: reward.name, description: reward.description, imageUrl: reward.imageUrl },
        Object.assign({}, adapter, { cache: { search: searchCache, product: productCache } })
      );
    };
    return rewardPresentationCache;
  }

  function resolveShopifyProductImages(products) {
    var resolver = getRewardPresentationResolver();
    if (!resolver) {
      return Promise.resolve(products);
    }
    var sortedProducts = products.slice();
    sortedProducts.sort(function (first, second) {
      var firstPoints = first.pointsPrice === null ? Number.POSITIVE_INFINITY : first.pointsPrice;
      var secondPoints = second.pointsPrice === null ? Number.POSITIVE_INFINITY : second.pointsPrice;
      return firstPoints - secondPoints;
    });

    function delay(ms) {
      return new Promise(function (resolve) {
        window.setTimeout(resolve, ms);
      });
    }

    return sortedProducts.reduce(function (chain, product, index) {
      return chain.then(function (resolvedProducts) {
        if (!isPhysicalReward(product)) {
          resolvedProducts.push(product);
          return resolvedProducts;
        }
        var start = index === 0 ? Promise.resolve() : delay(250);
        return start
          .then(function () {
            return resolver(product);
          })
          .then(function (presentation) {
            if (presentation && presentation.imageUrl && (presentation.matchLevel === 'product' || presentation.matchLevel === 'variant')) {
              product.imageUrl = presentation.imageUrl;
            }
            resolvedProducts.push(product);
            return resolvedProducts;
          })
          .catch(function () {
            resolvedProducts.push(product);
            return resolvedProducts;
          });
      });
    }, Promise.resolve([]));
  }

  function createCard(root, product) {
    var card = document.createElement('article');
    var imageWrap = document.createElement('div');
    var content = document.createElement('div');
    var title = document.createElement('h3');
    var points = document.createElement('p');
    var action = document.createElement('button');
    var actionLabel = document.createElement('span');
    var actionIcon = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    var actionIconPath = document.createElementNS('http://www.w3.org/2000/svg', 'path');

    card.className = 'smile-points-shop__card';
    imageWrap.className = 'smile-points-shop__image-wrap';
    content.className = 'smile-points-shop__card-content';
    title.className = 'smile-points-shop__product-name';
    points.className = 'smile-points-shop__points';
    action.className = 'smile-points-shop__action';
    action.type = 'button';
    action.dataset.smilePointsProductId = product.id;
    actionIcon.classList.add('smile-points-shop__action-arrow');
    actionIcon.setAttribute('width', '12');
    actionIcon.setAttribute('height', '12');
    actionIcon.setAttribute('viewBox', '0 0 12 12');
    actionIcon.setAttribute('fill', 'none');
    actionIcon.setAttribute('aria-hidden', 'true');
    actionIcon.setAttribute('focusable', 'false');
    actionIconPath.setAttribute('d', 'M2.5 6H9.5M9.5 6L6 2.5M9.5 6L6 9.5');
    actionIconPath.setAttribute('stroke', 'currentColor');
    actionIconPath.setAttribute('stroke-width', '1.5');
    actionIconPath.setAttribute('stroke-linecap', 'round');
    actionIconPath.setAttribute('stroke-linejoin', 'round');
    actionIcon.appendChild(actionIconPath);

    if (product.imageUrl && !isPhysicalReward(product)) {
      var image = document.createElement('img');
      image.className = 'smile-points-shop__image';
      image.src = product.imageUrl;
      image.alt = product.name;
      image.width = 800;
      image.height = 600;
      image.loading = 'lazy';
      image.decoding = 'async';
      imageWrap.appendChild(image);
    } else {
      var fallback = document.createElement('span');
      fallback.className = 'smile-points-shop__image-fallback';
      fallback.textContent = isPhysicalReward(product) ? '' : 'GAN Rewards';
      imageWrap.appendChild(fallback);
    }

    title.textContent = product.name;
    points.textContent = product.pointsLabel;

    if (!isLoggedIn(root)) {
      actionLabel.textContent = 'Sign in to redeem';
    } else if (product.canRedeem) {
      actionLabel.textContent = 'Redeem';
    } else {
      actionLabel.textContent = 'Not enough points';
      action.disabled = true;
    }

    action.appendChild(actionLabel);
    action.appendChild(actionIcon);

    content.appendChild(title);
    content.appendChild(points);
    content.appendChild(action);
    card.appendChild(imageWrap);
    card.appendChild(content);

    return card;
  }

  function updateCardImage(root, product) {
    if (!product || !product.id || !product.imageUrl) return;
    if (isPhysicalReward(product)) return;
    var action = root.querySelector('[data-smile-points-shop-grid] [data-smile-points-product-id="' + product.id + '"]');
    var card = action && action.closest('.smile-points-shop__card');
    if (!card) return;

    var imageWrap = card.querySelector('.smile-points-shop__image-wrap');
    if (!imageWrap) return;

    var image = imageWrap.querySelector('.smile-points-shop__image');
    if (image) {
      if (image.src !== product.imageUrl) image.src = product.imageUrl;
      image.alt = product.name;
      return;
    }

    imageWrap.replaceChildren();
    image = document.createElement('img');
    image.className = 'smile-points-shop__image';
    image.src = product.imageUrl;
    image.alt = product.name;
    image.width = 800;
    image.height = 600;
    image.loading = 'lazy';
    image.decoding = 'async';
    imageWrap.appendChild(image);
  }

  function applyResolvedProductImages(root, products) {
    if (!root.isConnected || !Array.isArray(products)) return;
    var cacheableProducts = [];
    products.forEach(function (product) {
      updateCardImage(root, product);
      if (!isPhysicalReward(product) || product.imageUrl) {
        cacheableProducts.push(product);
      }
    });
    storeCachedProducts(root, cacheableProducts);
  }

  function updateCarouselControls(root) {
    var grid = root.querySelector('[data-smile-points-shop-grid]');
    var previous = root.querySelector('[data-smile-carousel-direction="previous"]');
    var next = root.querySelector('[data-smile-carousel-direction="next"]');
    if (!grid || !previous || !next) return;

    var maximumScroll = Math.max(0, grid.scrollWidth - grid.clientWidth);
    previous.disabled = grid.scrollLeft <= 1;
    next.disabled = grid.scrollLeft >= maximumScroll - 1;
  }

  function scrollCarousel(root, direction) {
    var grid = root.querySelector('[data-smile-points-shop-grid]');
    if (!grid) return;

    var firstCard = grid.querySelector('.smile-points-shop__card');
    var styles = window.getComputedStyle(grid);
    var gap = parseFloat(styles.columnGap || styles.gap) || 0;
    var cardWidth = firstCard ? firstCard.getBoundingClientRect().width : grid.clientWidth;
    var visibleCards = Math.max(1, Math.floor((grid.clientWidth + gap) / (cardWidth + gap)));
    var distance = (cardWidth + gap) * visibleCards;

    grid.scrollBy({
      left: direction === 'previous' ? -distance : distance,
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
    });
  }

  function renderProducts(root, products) {
    var grid = root.querySelector('[data-smile-points-shop-grid]');
    var fragment = document.createDocumentFragment();

    grid.replaceChildren();
    root.smilePointsShopProducts = {};
    products.forEach(function (product) {
      if (product.id) {
        root.smilePointsShopProducts[product.id] = product;
        fragment.appendChild(createCard(root, product));
      }
    });
    grid.appendChild(fragment);

    if (!grid.children.length) {
      setState(root, 'empty', 'No rewards are available right now.');
      return false;
    }

    setState(root, 'ready', '');
    grid.scrollLeft = 0;
    window.requestAnimationFrame(function () {
      updateCarouselControls(root);
    });
    announce(root, grid.children.length + ' rewards loaded.');
    return true;
  }

  function readPendingProduct(root) {
    try {
      var pending = JSON.parse(window.sessionStorage.getItem(PENDING_PRODUCT_KEY));
      if (!pending || pending.path !== root.dataset.returnPath || !pending.id) return null;
      return String(pending.id);
    } catch (error) {
      return null;
    }
  }

  function productsCacheKey(root) {
    return PRODUCT_CACHE_KEY + ':' + (isLoggedIn(root) ? 'customer' : 'guest') + ':' + (root.dataset.returnPath || 'default');
  }

  function readCachedProducts(root) {
    try {
      var raw = window.sessionStorage.getItem(productsCacheKey(root));
      if (!raw) return null;
      var parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return null;
      return parsed.filter(function (product) {
        return product && product.id && product.name;
      });
    } catch (error) {
      return null;
    }
  }

  function storeCachedProducts(root, products) {
    if (!Array.isArray(products) || !products.length) return;
    try {
      window.sessionStorage.setItem(productsCacheKey(root), JSON.stringify(products));
    } catch (error) {
      // Cache is best-effort only.
    }
  }

  function storePendingProduct(root, productId) {
    try {
      window.sessionStorage.setItem(
        PENDING_PRODUCT_KEY,
        JSON.stringify({ id: String(productId), path: root.dataset.returnPath })
      );
    } catch (error) {
      // Login still works when session storage is unavailable; only auto-resume is skipped.
    }
  }

  function clearPendingProduct() {
    try {
      window.sessionStorage.removeItem(PENDING_PRODUCT_KEY);
    } catch (error) {
      // No action is needed when storage is unavailable.
    }
  }

  function resetRedeemModal(root) {
    var modal = root.querySelector('[data-smile-redeem-modal]');
    if (!modal) return;

    modal.querySelector('[data-smile-redeem-form]').hidden = false;
    modal.querySelector('[data-smile-redeem-success]').hidden = true;
    modal.querySelector('[data-smile-redeem-error]').hidden = true;
    modal.querySelector('[data-smile-redeem-error]').textContent = '';
    modal.querySelector('[data-smile-redeem-code-wrap]').hidden = false;
    modal.querySelector('[data-smile-redeem-code]').textContent = '';
    modal.querySelector('[data-smile-redeem-copy]').textContent = 'Copy code';
  }

  function rewardConditions(description) {
    if (!description) return [];

    var content = document.createElement('div');
    content.innerHTML = description;
    var listItems = Array.from(content.querySelectorAll('li'))
      .map(function (item) {
        return item.textContent.trim();
      })
      .filter(Boolean);

    if (listItems.length) return listItems;

    var text = content.textContent.replace(/\s+/g, ' ').trim();
    return (text.match(/[^.!?]+(?:[.!?]+|$)/g) || [])
      .map(function (condition) {
        return condition.trim();
      })
      .filter(Boolean);
  }

  function openRedeemModal(root, product, trigger) {
    var modal = root.querySelector('[data-smile-redeem-modal]');
    if (!modal || !product) return;

    resetRedeemModal(root);
    root.smilePointsShopActiveProduct = product;
    root.smilePointsShopActiveTrigger = trigger || null;

    var productImage = modal.querySelector('[data-smile-redeem-product-image]');
    productImage.src = product.imageUrl;
    productImage.alt = product.name;
    modal.querySelector('[data-smile-redeem-product-name]').textContent = product.name;
    modal.querySelector('[data-smile-redeem-points]').textContent = product.pointsLabel;

    var conditions = modal.querySelector('[data-smile-redeem-conditions]');
    var conditionsList = modal.querySelector('[data-smile-redeem-conditions-list]');
    var conditionItems = rewardConditions(product.description);
    conditionsList.replaceChildren();
    conditions.hidden = !conditionItems.length;
    conditionItems.forEach(function (conditionText) {
      var condition = document.createElement('li');
      condition.textContent = conditionText;
      conditionsList.appendChild(condition);
    });

    var confirm = modal.querySelector('[data-smile-redeem-confirm]');
    confirm.disabled = false;
    confirm.textContent = 'Redeem ' + product.name;

    if (typeof modal.showModal === 'function') modal.showModal();
  }

  function resumePendingProduct(root) {
    if (!isLoggedIn(root) || root.smilePointsShopResumeAttempted) return;

    var productId = readPendingProduct(root);
    if (!productId) return;

    root.smilePointsShopResumeAttempted = true;
    var product = root.smilePointsShopProducts && root.smilePointsShopProducts[productId];
    if (!product) return;

    clearPendingProduct();
    openRedeemModal(root, product, null);
  }

  function closeRedeemModal(root) {
    var modal = root.querySelector('[data-smile-redeem-modal]');
    if (modal && modal.open) modal.close();
  }

  function purchaseReward(root) {
    var modal = root.querySelector('[data-smile-redeem-modal]');
    var product = root.smilePointsShopActiveProduct;
    var confirm = modal && modal.querySelector('[data-smile-redeem-confirm]');
    var errorMessage = modal && modal.querySelector('[data-smile-redeem-error]');
    var purchase = window.Smile && window.Smile.pointsProducts && window.Smile.pointsProducts.purchase;

    if (!modal || !product || !confirm || typeof purchase !== 'function') {
      if (errorMessage) {
        errorMessage.textContent = 'This reward cannot be redeemed right now. Please try again later.';
        errorMessage.hidden = false;
      }
      return;
    }

    confirm.disabled = true;
    confirm.textContent = 'Redeeming...';
    errorMessage.hidden = true;

    Promise.resolve(purchase.call(window.Smile.pointsProducts, Number(product.id)))
      .then(function (pointsPurchase) {
        var fulfillment = pointsPurchase.rewardFulfillment || pointsPurchase.reward_fulfillment || {};
        var code = fulfillment.code || '';

        modal.querySelector('[data-smile-redeem-form]').hidden = true;
        modal.querySelector('[data-smile-redeem-success]').hidden = false;
        modal.querySelector('[data-smile-redeem-code]').textContent = code;
        modal.querySelector('[data-smile-redeem-code-wrap]').hidden = !code;
        announce(root, 'Reward redeemed successfully.');
      })
      .catch(function (error) {
        confirm.disabled = false;
        confirm.textContent = 'Redeem ' + product.name;
        errorMessage.textContent = 'This reward could not be redeemed. Please check your points and try again.';
        errorMessage.hidden = false;
        console.error('[Smile Points Shop]', error);
      });
  }

  function copyRewardCode(root) {
    var modal = root.querySelector('[data-smile-redeem-modal]');
    var code = modal && modal.querySelector('[data-smile-redeem-code]').textContent;
    var copyButton = modal && modal.querySelector('[data-smile-redeem-copy]');
    if (!code || !copyButton || !navigator.clipboard) return;

    navigator.clipboard.writeText(code).then(function () {
      copyButton.textContent = 'Copied';
      announce(root, 'Reward code copied.');
    });
  }

  function scheduleSdkTimeout(root) {
    if (root.smilePointsShopTimeout) window.clearTimeout(root.smilePointsShopTimeout);

    root.smilePointsShopTimeout = window.setTimeout(function () {
      if (!root.isConnected || root.dataset.smilePointsShopState === 'ready') return;
      root.dataset.smilePointsShopState = 'error';
      setState(root, 'error', 'Rewards are temporarily unavailable. Please try again.');
    }, SDK_TIMEOUT_MS);
  }

  function scheduleSdkPoll(root) {
    if (root.smilePointsShopSdkPoll || !root.isConnected) return;

    root.smilePointsShopSdkPoll = window.setTimeout(function () {
      root.smilePointsShopSdkPoll = null;
      if (!root.isConnected) return;

      if (hasSmileMethods(root)) {
        loadProducts(root, 0);
        return;
      }

      scheduleSdkPoll(root);
    }, SDK_POLL_MS);
  }

  function startLoading(root) {
    if (!root.isConnected || root.smilePointsShopLoading) return;

    var cachedProducts = readCachedProducts(root);
    if (cachedProducts && cachedProducts.length) {
      renderProducts(root, cachedProducts);
      resumePendingProduct(root);
      loadProducts(root, 0);
      return;
    }

    root.dataset.smilePointsShopState = 'loading';
    setState(root, 'loading', 'Loading rewards...');
    scheduleSdkTimeout(root);
    loadProducts(root, 0);
  }

  function loadProducts(root, customerAttempt) {
    if (!root.isConnected || root.smilePointsShopLoading) return;
    if (!hasSmileMethods(root)) {
      scheduleSdkPoll(root);
      return;
    }

    if (root.smilePointsShopSdkPoll) {
      window.clearTimeout(root.smilePointsShopSdkPoll);
      root.smilePointsShopSdkPoll = null;
    }

    var hasRenderedProducts = Boolean(root.smilePointsShopProducts && Object.keys(root.smilePointsShopProducts).length);

    root.smilePointsShopLoading = true;
    if (!hasRenderedProducts) {
      root.dataset.smilePointsShopState = 'loading';
      setState(root, 'loading', 'Loading rewards...');
      scheduleSdkTimeout(root);
    }

    var request;
    if (isLoggedIn(root)) {
      if (!window.Smile.customer.current()) {
        request = Promise.reject(new Error('Smile has not identified the signed-in customer yet.'));
      } else {
        request = Promise.all([
          window.Smile.customerPointsProducts.get(),
          window.Smile.customerPointsWallet.get(),
        ]).then(function (results) {
          var balance = Number(results[1] && results[1].balance);
          if (Number.isNaN(balance)) throw new Error('Smile customer points balance is unavailable.');

          return results[0].map(function (item) {
            return normalizeProduct(item, balance, true);
          });
        });
      }
    } else {
      request = window.Smile.pointsProducts.get().then(function (items) {
        return items.map(function (item) {
          return normalizeProduct(item, 0, false);
        });
      });
    }

    request
      .then(function (products) {
        if (!root.isConnected) return;
        root.dataset.smilePointsShopState = products.length ? 'ready' : 'empty';
        if (!renderProducts(root, products)) return;
        storeCachedProducts(root, products);
        resumePendingProduct(root);

        resolveShopifyProductImages(products)
          .then(function (resolvedProducts) {
            applyResolvedProductImages(root, resolvedProducts);
          })
          .catch(function (error) {
            console.error('[Smile Points Shop] image resolution failed', error);
          });
      })
      .catch(function (error) {
        if (!root.isConnected) return;

        var maxAttempts = isLoggedIn(root) ? MAX_CUSTOMER_ATTEMPTS : MAX_GUEST_ATTEMPTS;
        var retryDelay = isLoggedIn(root) ? CUSTOMER_RETRY_MS : GUEST_RETRY_MS;
        if (customerAttempt < maxAttempts) {
          window.setTimeout(function () {
            root.smilePointsShopLoading = false;
            loadProducts(root, customerAttempt + 1);
          }, retryDelay);
          return;
        }

        root.dataset.smilePointsShopState = 'error';
        setState(root, 'error', 'Rewards are temporarily unavailable. Please try again.');
        console.error('[Smile Points Shop]', error);
      })
      .finally(function () {
        root.smilePointsShopLoading = false;
      });
  }

  function handleClick(root, event) {
    var closeModal = event.target.closest('[data-smile-redeem-close]');
    if (closeModal) {
      closeRedeemModal(root);
      return;
    }

    if (event.target.closest('[data-smile-redeem-confirm]')) {
      purchaseReward(root);
      return;
    }

    if (event.target.closest('[data-smile-redeem-copy]')) {
      copyRewardCode(root);
      return;
    }

    var carouselButton = event.target.closest('[data-smile-carousel-direction]');
    if (carouselButton && !carouselButton.disabled) {
      scrollCarousel(root, carouselButton.dataset.smileCarouselDirection);
      return;
    }

    var retry = event.target.closest('[data-smile-points-shop-retry]');
    if (retry) {
      root.smilePointsShopLoading = false;
      startLoading(root);
      return;
    }

    var action = event.target.closest('.smile-points-shop__action[data-smile-points-product-id]');
    if (!action || action.disabled) return;

    var productId = action.dataset.smilePointsProductId;
    if (!productId) return;

    if (!isLoggedIn(root)) {
      storePendingProduct(root, productId);
      window.location.assign(root.dataset.loginUrl);
      return;
    }

    var product = root.smilePointsShopProducts && root.smilePointsShopProducts[productId];
    openRedeemModal(root, product, action);
  }

  function initRoot(root) {
    if (!root || root.dataset.smilePointsShopInitialized === 'true') return;

    root.dataset.smilePointsShopInitialized = 'true';
    root.addEventListener('click', function (event) {
      handleClick(root, event);
    });
    var grid = root.querySelector('[data-smile-points-shop-grid]');
    if (grid) {
      grid.addEventListener('scroll', function () {
        if (root.smilePointsShopCarouselFrame) return;
        root.smilePointsShopCarouselFrame = window.requestAnimationFrame(function () {
          root.smilePointsShopCarouselFrame = null;
          updateCarouselControls(root);
        });
      });
    }
    window.addEventListener('resize', function () {
      updateCarouselControls(root);
    });
    var modal = root.querySelector('[data-smile-redeem-modal]');
    if (modal) {
      modal.addEventListener('click', function (event) {
        if (event.target === modal) closeRedeemModal(root);
      });
      modal.addEventListener('close', function () {
        if (root.smilePointsShopActiveTrigger) root.smilePointsShopActiveTrigger.focus();
      });
    }
    startLoading(root);
  }

  function init(scope) {
    var target = scope || document;
    if (target.matches && target.matches(ROOT_SELECTOR)) initRoot(target);
    target.querySelectorAll(ROOT_SELECTOR).forEach(initRoot);
  }

  window.GanSmilePointsShop = { init: init };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init(document);
    });
  } else {
    init(document);
  }

  document.addEventListener('smile-js-initialized', function () {
    document.querySelectorAll(ROOT_SELECTOR).forEach(function (root) {
      root.smilePointsShopLoading = false;
      startLoading(root);
    });
  });

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
})();
